/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { Button, ModalConfirm, Text, theme } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { showSuccess } from "~/components/Toast";
import { EnvironmentRouteParams } from "~/models";
import { AssignmentRuleRouteParams } from "~/models/assignmentRule";
import { useAxios, generateActionSuccessText } from "~/tools";
import { ServiceItem } from "./types";

type ServiceDeactivateProps = {
  refetch: () => void;
  service: ServiceItem;
  isDeactivated: boolean;
  isAssignmentRule?: boolean;
  setIsPopover: React.Dispatch<React.SetStateAction<boolean>>;
};

function ServiceDeactivate({
  refetch,
  service,
  isDeactivated,
  setIsPopover,
  isAssignmentRule,
}: ServiceDeactivateProps) {
  const state = useParams<EnvironmentRouteParams & AssignmentRuleRouteParams>();
  const { environmentId, customerId, assignmentRuleId } = state;

  const [isOpen, setIsOpen] = React.useState(false);

  const nameForToast = service?.name;
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const actionName = isDeactivated ? "Activate" : "Deactivate";

  const handleClose = () => {
    setIsPopover(false);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        css={{ backgroundColor: theme.color.background.ui01, width: "100%" }}
        size="md"
        icon={isDeactivated ? "plus" : "minus"}
        severity="low"
      >
        {actionName}
      </Button>
      <ModalConfirm
        onClose={handleClose}
        isOpen={isOpen}
        confirm={async () => {
          const action = {
            deactivated: !isDeactivated,
          };

          try {
            isAssignmentRule
              ? await Axios().put(
                  `/customers/${customerId}/assignmentRules/${assignmentRuleId}/services/${service?.id}`,
                  action
                )
              : await Axios().put(
                  `/environments/${environmentId}/services/${service.id}`,
                  action
                );
            setIsOpen(true);
            refetch();
            showSuccess(
              generateActionSuccessText(`The ${nameForToast}`)()(
                `${actionName}d`
              )()
            );
          } catch (e) {
            showError(e.response);
          } finally {
            handleClose();
          }
        }}
        actionLabel={actionName}
        contentLabel={actionName}
      >
        <Text color="warning">
          {`Are you sure you want to ${actionName} ${nameForToast}`}
        </Text>
      </ModalConfirm>
    </>
  );
}

export { ServiceDeactivate };
